import { useEffect, useMemo, useState, type CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  cn,
} from "@nexus/ui"
import {
  WHY_BAND_THRESHOLDS,
  type DrawerWhyKpiOption,
  type DrawerWhySection,
  type TopicExampleRow,
} from "@nexus/contracts"
import type { DrilldownPageFiltersInput } from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import { useWhyTagExamples } from "../../hooks/useMyBrandDrawerWhyData"
import {
  buildWhyInsight,
  exampleKpiValue,
  exampleName,
  formatWhyDelta,
  formatWhyKpiValue,
  formatWhyWindow,
  readStoredWhyKpi,
  resolveWhyKpi,
  storeWhyKpi,
  whyAxisPercent,
  whyRowsForKpi,
  whySubFeatureLabel,
  whyTagKey,
  whyTagLabel,
  type WhyTagView,
} from "../../lib/my-brand-drawer-why"

import type { TFunction } from "i18next"

// Delta tones reuse the Performance tab's KPI badge colours (UserKpiBox TONE_CLASS).
const TONE_TEXT = {
  helping: "text-green-600 dark:text-green-400",
  hurting: "text-red-600 dark:text-red-400",
  low: "text-muted-foreground",
} as const
const TONE_DOT = {
  helping: "bg-green-600 dark:bg-green-400",
  hurting: "bg-red-600 dark:bg-red-400",
  low: "bg-muted-foreground",
} as const
const TONE_BAR = {
  helping: "border-green-500/40 bg-green-500/10",
  hurting: "border-red-500/40 bg-red-500/10",
  low: "border-border bg-muted",
} as const

const EXAMPLES_SHOWN = 5

interface DrilldownMyBrandCreativeWhyTabProps {
  section: DrawerWhySection | null
  isLoading: boolean
  isError: boolean
  /** Loaded, but the tenant has no tag data to read (no customer partition). */
  isUnavailable?: boolean
  /** Page North Star (`roas` / `cpa` / `custom`): the KPI shown until the person picks one. */
  northStar: string
  currencySymbol: string
  currentHash: string
  tenantId: string | null
  myBrandFilters: MyBrandFiltersInput
  drilldownFilters: DrilldownPageFiltersInput
  /** Opens an example creative in this same drawer. */
  onOpenExample?: (row: TopicExampleRow) => void
  /** Set after a jump from an example: one-level way back. */
  backLabel?: string | null
  onBack?: () => void
}

export function DrilldownMyBrandCreativeWhyTab({
  section,
  isLoading,
  isError,
  isUnavailable = false,
  northStar,
  currencySymbol,
  currentHash,
  tenantId,
  myBrandFilters,
  drilldownFilters,
  onOpenExample,
  backLabel = null,
  onBack,
}: DrilldownMyBrandCreativeWhyTabProps) {
  const { t, i18n } = useTranslation()
  const summary = section?.summary ?? null
  // The person's last KPI pick, kept per tenant across creatives and sessions. Read again when
  // the tenant changes under a mounted tab, so another tenant's pick never carries over.
  const [chosenKpi, setChosenKpi] = useState<string | null>(() => readStoredWhyKpi(tenantId))
  useEffect(() => {
    setChosenKpi(readStoredWhyKpi(tenantId))
  }, [tenantId])
  const option = resolveWhyKpi(summary?.kpis ?? [], chosenKpi, northStar)
  const rows = useMemo(
    () => (section && option ? whyRowsForKpi(section.rows, option) : []),
    [section, option],
  )
  const lowerIsBetter = option?.lowerIsBetter ?? true
  const kpiLabel = option?.label ?? ""
  const pickKpi = (key: string) => {
    setChosenKpi(key)
    storeWhyKpi(tenantId, key)
  }
  const insight = useMemo(
    () => buildWhyInsight(rows, kpiLabel, lowerIsBetter, t),
    [rows, kpiLabel, lowerIsBetter, t],
  )

  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  // Low impact is the least interesting group: folded by default, per creative.
  const [lowOpen, setLowOpen] = useState(false)
  useEffect(() => {
    const first = insight.named[0] ?? rows[0]
    setSelectedKey(first ? whyTagKey(first) : null)
    setLowOpen(false)
  }, [rows, insight.named])
  const selected = rows.find((r) => whyTagKey(r) === selectedKey) ?? null

  const formatKpi = (value: number | null) =>
    option ? formatWhyKpiValue(value, option.format, currencySymbol) : "—"

  if (isError) {
    return (
      <p className="px-6 py-5 text-sm text-destructive">
        {t("drilldown.myBrandDrawer.why.loadError", "Failed to load tag data.")}
      </p>
    )
  }

  if (isUnavailable) {
    return (
      <p className="px-6 py-5 text-sm text-muted-foreground">
        {t("drilldown.myBrandDrawer.why.unavailable", "Tag data is not set up for this account yet.")}
      </p>
    )
  }

  if (isLoading || !section || !summary) {
    return (
      <div className="flex flex-col gap-3 px-6 py-5" aria-busy="true">
        <Skeleton className="h-20 w-full rounded-xl" />
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded" />
        ))}
      </div>
    )
  }

  // The page filters leave no creative in the window: say so, not "no tags" or "no KPI data".
  if (summary.windowCreatives === 0) {
    return (
      <p className="px-6 py-5 text-sm text-muted-foreground">
        {t("drilldown.myBrandDrawer.why.noMatchingCreatives", "No creatives match these filters.")}
      </p>
    )
  }

  // No KPI of the list has data in the window (no conversions, value, impressions …).
  if (!option) {
    return (
      <p className="px-6 py-5 text-sm text-muted-foreground">
        {t("drilldown.myBrandDrawer.why.noKpiWithData", "None of your KPIs has data for this window.")}
      </p>
    )
  }

  if (rows.length === 0) {
    return (
      <p className="px-6 py-5 text-sm text-muted-foreground">
        {t("drilldown.myBrandDrawer.why.empty", "No tags found for this creative in this date range.")}
      </p>
    )
  }

  const caption = t(
    "drilldown.myBrandDrawer.why.caption",
    "Against {{count}} of your own creatives, {{window}}. Account baseline for the same period: {{kpi}} {{baseline}}.",
    {
      count: summary.windowCreatives,
      window: formatWhyWindow(summary.startDate, summary.endDate, t, i18n.language),
      kpi: kpiLabel,
      baseline: formatKpi(option.baseline),
    },
  )
  // Metric rules the tab could not apply (e.g. "Spend %"), named as the "+ Filter" pill shows them.
  const ignoredNote =
    summary.ignoredFilterFields.length > 0
      ? t("drilldown.myBrandDrawer.why.filtersNotApplied", "Not applied in this tab: {{fields}}.", {
          fields: summary.ignoredFilterFields.join(", "),
        })
      : null
  const bandLabel = {
    helping: t("drilldown.myBrandDrawer.why.helping", "Helping"),
    hurting: t("drilldown.myBrandDrawer.why.hurting", "Hurting"),
    low: t("drilldown.myBrandDrawer.why.lowImpact", "Low impact"),
  }
  const rowsIn = (band: WhyTagView["band"]) =>
    rows.filter((r) => r.band === band).sort((a, b) => Number(a.lowSample) - Number(b.lowSample))
  const helpingRows = rowsIn("helping")
  const hurtingRows = rowsIn("hurting")
  const lowRows = rowsIn("low")
  const renderRows = (bandRows: WhyTagView[]) =>
    bandRows.map((row) => (
      <WhyTagRowView
        key={whyTagKey(row)}
        row={row}
        lowerIsBetter={lowerIsBetter}
        selected={whyTagKey(row) === selectedKey}
        onSelect={() => setSelectedKey(whyTagKey(row))}
      />
    ))
  const bandHeader = "pb-1 pt-3 text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground"

  return (
    <div className="@container flex flex-col gap-6 px-6 py-5">
      {backLabel && onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="-mb-3 inline-flex items-center gap-1 self-start text-[12px] text-primary hover:underline"
        >
          <Icon name="arrow-left" size="xs" aria-hidden="true" />
          {t("drilldown.myBrandDrawer.why.backTo", "Back to {{name}}", { name: backLabel })}
        </button>
      ) : null}
      <WhyInsightPanel insight={insight} selectedKey={selectedKey} onSelect={setSelectedKey} />

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h3 className="flex items-baseline gap-1 text-[13px] font-medium text-foreground">
            {t("drilldown.myBrandDrawer.why.effectTitlePrefix", "Effect of each tag on")}
            <WhyKpiPicker options={summary.kpis} selected={option} onSelect={pickKpi} />
          </h3>
          <div className="flex flex-col gap-0.5">
            <p className="text-[11px] text-muted-foreground">{caption}</p>
            {ignoredNote ? <p className="text-[11px] text-muted-foreground">{ignoredNote}</p> : null}
          </div>
        </div>

        <div className="flex flex-col">
          {helpingRows.length > 0 ? (
            <div className="flex flex-col">
              <p className={bandHeader}>{bandLabel.helping}</p>
              {renderRows(helpingRows)}
            </div>
          ) : null}
          <div className="flex flex-col">
            <p className={bandHeader}>{bandLabel.hurting}</p>
            {hurtingRows.length > 0 ? (
              renderRows(hurtingRows)
            ) : (
              <p className="px-2 py-1.5 text-[12px] text-muted-foreground">
                {lowerIsBetter
                  ? t("drilldown.myBrandDrawer.why.noHurting", "No tag in this creative runs above your baseline")
                  : t("drilldown.myBrandDrawer.why.noHurtingRoas", "No tag in this creative runs below your baseline")}
              </p>
            )}
          </div>
          {lowRows.length > 0 ? (
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => setLowOpen((v) => !v)}
                aria-expanded={lowOpen}
                className={cn(bandHeader, "inline-flex items-center gap-1 self-start hover:text-foreground")}
              >
                <Icon name={lowOpen ? "chevron-down" : "chevron-right"} size="xs" aria-hidden="true" />
                {t("drilldown.myBrandDrawer.why.lowImpactTags", { count: lowRows.length })}
              </button>
              {lowOpen ? renderRows(lowRows) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className={cn("size-2 rounded-full", TONE_DOT.helping)} aria-hidden="true" />
            {t("drilldown.myBrandDrawer.why.legendDot", "Average of creatives with the tag")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className={cn("h-2 w-4 rounded-sm border", TONE_BAR.helping)} aria-hidden="true" />
            {t("drilldown.myBrandDrawer.why.legendBar", "Middle half of them")}
          </span>
        </div>
      </section>

      {selected ? (
        <WhyExamples
          tag={selected}
          option={option}
          currentHash={currentHash}
          tenantId={tenantId}
          myBrandFilters={myBrandFilters}
          drilldownFilters={drilldownFilters}
          onOpenExample={onOpenExample}
        />
      ) : null}
    </div>
  )
}

/** The KPI the tab reads, as a menu of the KPIs with data in the window. */
function WhyKpiPicker({
  options,
  selected,
  onSelect,
}: {
  options: DrawerWhyKpiOption[]
  selected: DrawerWhyKpiOption
  onSelect: (key: string) => void
}) {
  const { t } = useTranslation()
  if (options.length < 2) return <span>{selected.label}</span>
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("drilldown.myBrandDrawer.why.changeKpi", "Change KPI: {{kpi}}", { kpi: selected.label })}
          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-0.5 rounded px-1 text-primary hover:bg-primary/10 data-[state=open]:bg-primary/10 [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
        >
          {selected.label}
          <Icon name="chevron-down" size="xs" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuRadioGroup value={selected.key} onValueChange={onSelect}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.key} value={o.key}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function WhyInsightPanel({
  insight,
  selectedKey,
  onSelect,
}: {
  insight: ReturnType<typeof buildWhyInsight>
  selectedKey: string | null
  onSelect: (key: string) => void
}) {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-green-500/40 bg-green-500/5 px-4 py-3">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.06em] text-green-700 dark:text-green-400">
        <Icon name="sparkles" size="xs" aria-hidden="true" />
        {t("drilldown.myBrandDrawer.why.insightLabel", "Adge Insight")}
      </span>
      <p className="text-[13px] leading-relaxed text-foreground">{insight.text}</p>
      {insight.named.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {insight.named.map((row, i) => {
            const key = whyTagKey(row)
            const active = key === selectedKey
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                aria-pressed={active}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-[12px] text-foreground",
                  active ? "border-primary bg-primary/10" : "border-border hover:bg-muted/60",
                )}
              >
                <span className={cn("size-1.5 rounded-full", TONE_DOT[row.band])} aria-hidden="true" />
                {insight.names[i] ?? whyTagLabel(row)}
                <span className={cn("tabular-nums", TONE_TEXT[row.band])}>{formatWhyDelta(row.delta)}</span>
              </button>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

// The label column keeps up to 200px; at narrow widths the bar column gives way (down to 56px).
const ROW_GRID = "grid grid-cols-[minmax(0,200px)_minmax(56px,1fr)_48px] items-center gap-x-2"

function AxisGuides() {
  return (
    <>
      <span className="absolute inset-y-0 left-1/4 border-l border-dashed border-border/50" aria-hidden="true" />
      <span className="absolute inset-y-0 left-1/2 border-l border-foreground/30" aria-hidden="true" />
      <span className="absolute inset-y-0 left-3/4 border-l border-dashed border-border/50" aria-hidden="true" />
    </>
  )
}

function WhyTagRowView({
  row,
  lowerIsBetter,
  selected,
  onSelect,
}: {
  row: WhyTagView
  lowerIsBetter: boolean
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const tone = row.lowSample ? "low" : row.band
  const dot = whyAxisPercent(row.delta, lowerIsBetter)
  const a = whyAxisPercent(row.p25Delta, lowerIsBetter)
  const b = whyAxisPercent(row.p75Delta, lowerIsBetter)
  const barLeft = a !== null && b !== null ? Math.min(a, b) : null
  const barWidth = a !== null && b !== null ? Math.abs(a - b) : null

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        ROW_GRID,
        "rounded-md px-2 text-left",
        selected ? "bg-primary/10" : "hover:bg-muted/60",
        row.lowSample && "opacity-50",
      )}
    >
      <span className="flex min-w-0 flex-col py-1.5">
        {/* Labels and subtitles wrap rather than clip. */}
        <span className="break-words text-[13px] leading-snug text-foreground">{whyTagLabel(row)}</span>
        <span className="break-words text-[11px] leading-snug text-muted-foreground">
          {t("drilldown.myBrandDrawer.why.creativeCount", { count: row.creatives })}
          {" · "}
          {whySubFeatureLabel(row, t)}
        </span>
      </span>
      <span className="relative h-full min-h-9">
        <AxisGuides />
        {barLeft !== null && barWidth !== null ? (
          <>
            <span
              className={cn("absolute top-1/2 h-2.5 -translate-y-1/2 rounded-sm border", TONE_BAR[tone])}
              style={{ left: `${barLeft}%`, width: `${Math.max(barWidth, 0.5)}%` }}
              aria-hidden="true"
            />
            <span className="sr-only">{whyRangeSrText(row, t)}</span>
          </>
        ) : null}
        {dot !== null ? (
          <span
            className={cn("absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full", TONE_DOT[tone])}
            style={{ left: `${dot}%` }}
            aria-hidden="true"
          />
        ) : null}
      </span>
      <span className={cn("text-right text-[13px] tabular-nums", TONE_TEXT[tone])}>
        {formatWhyDelta(row.delta)}
      </span>
    </button>
  )
}

/** The bar's P25–P75 endpoints as text, low to high, for screen readers. */
function whyRangeSrText(row: WhyTagView, t: TFunction): string {
  const ends = [row.p25Delta, row.p75Delta].filter((d): d is number => d !== null).sort((a, b) => a - b)
  return t("drilldown.myBrandDrawer.why.rangeSr", "Middle half of creatives: {{low}} to {{high}} vs baseline.", {
    low: formatWhyDelta(ends[0] ?? null),
    high: formatWhyDelta(ends[1] ?? null),
  })
}

function WhyExamples({
  tag,
  option,
  currentHash,
  tenantId,
  myBrandFilters,
  drilldownFilters,
  onOpenExample,
}: {
  tag: WhyTagView
  option: DrawerWhyKpiOption
  currentHash: string
  tenantId: string | null
  myBrandFilters: MyBrandFiltersInput
  drilldownFilters: DrilldownPageFiltersInput
  onOpenExample?: (row: TopicExampleRow) => void
}) {
  const { t } = useTranslation()
  const { rows, isLoading, isError } = useWhyTagExamples(
    tenantId,
    tag.combinations,
    myBrandFilters,
    drilldownFilters,
  )
  const shown = rows.filter((r) => r.hash !== currentHash).slice(0, EXAMPLES_SHOWN)

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[13px] font-medium text-foreground">
        {t("drilldown.myBrandDrawer.why.examplesTitle", "Creatives that also have")}{" "}
        <span className="text-primary">{whyTagLabel(tag)}</span>
        <span className="text-muted-foreground">
          {" · "}
          {Math.max(0, tag.creatives - (tag.includesCurrent ? 1 : 0))}
        </span>
      </h3>

      {isError ? (
        <p className="text-sm text-destructive">
          {t("drilldown.myBrandDrawer.why.examplesError", "Failed to load creatives.")}
        </p>
      ) : (
        <div className="grid grid-cols-5 gap-2" aria-busy={isLoading}>
          {isLoading
            ? Array.from({ length: EXAMPLES_SHOWN }, (_, i) => (
                <div key={i} className="flex min-w-0 flex-col gap-1" aria-hidden="true">
                  <div
                    className="skeleton-shimmer-bar aspect-[3/4] w-full rounded-lg"
                    style={{ "--shimmer-delay": `${i * 80}ms` } as CSSProperties}
                  />
                  <div
                    className="skeleton-shimmer-bar h-3 w-3/4 rounded"
                    style={{ "--shimmer-delay": `${i * 80}ms` } as CSSProperties}
                  />
                </div>
              ))
            : shown.map((row) => {
                const value = exampleKpiValue(row, option)
                const delta = value !== null ? (value - option.baseline) / option.baseline : null
                const goodness = delta === null ? 0 : option.lowerIsBetter ? -delta : delta
                const lowImpactPoints = Math.round(WHY_BAND_THRESHOLDS.LOW_IMPACT_ABS_DELTA * 100)
                const tone =
                  delta === null || Math.round(Math.abs(delta) * 100) < lowImpactPoints
                    ? "low"
                    : goodness > 0
                      ? "helping"
                      : "hurting"
                return (
                  <figure key={row.hash} className="flex min-w-0 flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => onOpenExample?.(row)}
                      disabled={!onOpenExample}
                      aria-label={t("drilldown.myBrandDrawer.why.openExample", "Open {{name}}", { name: exampleName(row) })}
                      className="relative aspect-[3/4] overflow-hidden rounded-lg border border-border bg-muted/40 enabled:hover:border-primary"
                    >
                      {row.thumbnailUrl ? (
                        <img
                          src={row.thumbnailUrl}
                          alt=""
                          loading="lazy"
                          className="absolute inset-0 size-full object-cover"
                        />
                      ) : null}
                      <span
                        className={cn(
                          "absolute bottom-1.5 left-1.5 rounded bg-card/90 px-1 text-[10px] tabular-nums",
                          TONE_TEXT[tone],
                        )}
                      >
                        {formatWhyDelta(delta)}
                      </span>
                    </button>
                    <figcaption className="truncate text-[12px] text-foreground" title={exampleName(row)}>
                      {exampleName(row)}
                    </figcaption>
                  </figure>
                )
              })}
        </div>
      )}
    </section>
  )
}
